import type { ReactNode } from 'react';
import { useState } from 'react';

import { CircularProgress, Typography } from '@mui/material';
import { ChevronDownDashboardIcon as ChevronDownIcon } from '@truvhq/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import type { ChecklistStep, StepStatus } from 'entities/onboarding/types';
import { blockingStepId } from 'features/onboarding/lib/checklist';
import { useOnboardingChecklist } from 'features/onboarding/lib/useOnboardingChecklist';
import { ChecklistStepActions, ChecklistStepRow, COLLAPSE_TRANSITION } from 'features/onboarding/ui/ChecklistStepRow';

// What the accordion is showing. `null` follows the current step as the checklist advances; a step
// id pins that step open; COLLAPSED is the user having closed the one that was open.
const COLLAPSED = Symbol('collapsed');
type Expansion = string | typeof COLLAPSED | null;

/**
 * The setup checklist as a region of the assistant panel.
 *
 * `children` receives the open list and decides where it goes, because only the conversation knows
 * where its thread ends and its composer begins — the list covers the thread and leaves the composer
 * reachable. The conversation stays mounted underneath, so its scroll position and any in-flight
 * stream survive opening and closing the list.
 */
export type ChecklistSlot = {
  /** The open list, or null. The conversation places it over its thread. */
  overlay: ReactNode;
  /** Called when the user asks the assistant something, so the list gets out of the way. */
  onAsk: () => void;
};

export function ChecklistPane({ children }: { children: (slot: ChecklistSlot) => ReactNode }) {
  const checklist = useOnboardingChecklist();
  const [isListOpen, setIsListOpen] = useState<boolean | null>(null);
  const [expansion, setExpansion] = useState<Expansion>(null);
  const prefersReducedMotion = useReducedMotion();

  if (checklist.status === 'unavailable') {
    return <>{children({ overlay: null, onAsk: () => undefined })}</>;
  }

  if (checklist.status === 'loading') {
    return (
      <>
        <div className="flex flex-none items-center gap-2 border-grey30 border-b px-6 py-3">
          <CircularProgress size={16} />
          <Typography variant="body2" className="text-grey60">
            Loading your setup steps…
          </Typography>
        </div>
        {children({ overlay: null, onAsk: () => undefined })}
      </>
    );
  }

  const { sections, total_count, display_mode } = checklist.checklist;
  const { steps, current } = checklist;
  // `narrative` keeps the list collapsed to the header on open; `full` shows it. Until the user
  // touches the toggle, the server's preference wins.
  const listOpen = isListOpen ?? display_mode === 'full';
  const expandedStepId = expansion === COLLAPSED ? null : (expansion ?? current?.step_id ?? null);
  const isHeaderDetailOpen = !listOpen && Boolean(current);
  const slide = prefersReducedMotion ? { duration: 0 } : COLLAPSE_TRANSITION;

  const closeList = () => setIsListOpen(false);
  const toggleList = () => setIsListOpen(!listOpen);

  const settle = (step: ChecklistStep, status: StepStatus) =>
    checklist.settle(step, status).then((changed) => {
      if (!changed) {
        return;
      }
      // Ticking a step off moves the user on, so the expansion follows to whatever is current now.
      // Undo is the opposite: they're going back to this step, so it stays open under them.
      setExpansion(status === 'pending' ? step.step_id : null);
    });

  return (
    <>
      {/* Sits above the list so the list slides away underneath it rather than across it. */}
      <div className="relative z-[6] flex-none border-grey30 border-b bg-white pb-3">
        <button
          type="button"
          onClick={toggleList}
          aria-expanded={listOpen}
          className="flex w-full cursor-pointer items-center gap-2.5 bg-transparent px-6 pt-3 pb-1 text-left"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            {current && (
              <Typography variant="body2" className="font-medium text-grey60 text-xs">
                {`Step ${steps.indexOf(current) + 1} of ${total_count}`}
              </Typography>
            )}
            <Typography className="truncate font-medium text-grey90 text-sm">
              {current?.title ?? 'Setup complete'}
            </Typography>
          </span>
          <motion.span
            className="flex shrink-0"
            animate={{ rotate: listOpen ? 180 : 0 }}
            transition={slide}
            aria-hidden
          >
            <ChevronDownIcon className="h-4 w-4 text-grey60" />
          </motion.span>
        </button>
        {/* Collapsed, the header is the current step: its detail comes with it so the step stays
            actionable without reopening the list. */}
        <motion.div
          className="overflow-hidden"
          initial={false}
          animate={{ height: isHeaderDetailOpen ? 'auto' : 0, opacity: isHeaderDetailOpen ? 1 : 0 }}
          transition={slide}
          aria-hidden={!isHeaderDetailOpen}
        >
          <div className="flex flex-col px-6">
            {current?.description && (
              <Typography variant="body2" className="text-grey90">
                {current.description}
              </Typography>
            )}
            {current && (
              <ChecklistStepActions
                step={current}
                blockerTitle={checklist.blockerTitle(current)}
                onOpenBlocker={() => setExpansion(blockingStepId(current))}
                busy={checklist.pendingAction(current.step_id)}
                onSettle={(status) => settle(current, status)}
              />
            )}
          </div>
        </motion.div>
      </div>

      {children({
        onAsk: closeList,
        // Kept mounted through its exit so closing slides the list up out of the conversation.
        overlay: (
          <AnimatePresence>
            {listOpen && (
              <motion.section
                aria-label="Setup steps"
                className="absolute inset-0 z-[5] overflow-y-auto bg-grey20 px-4 py-4"
                initial={{ y: '-100%' }}
                animate={{ y: 0 }}
                exit={{ y: '-100%' }}
                transition={slide}
              >
                <div className="flex flex-col gap-4">
                  {sections.map((section) => (
                    <div key={section.section_id} className="flex flex-col gap-2">
                      {/* A single section reads as "the checklist"; more than one (dashboard plus an
                      integration) needs its headings to stay meaningful. */}
                      {sections.length > 1 && (
                        <Typography variant="body2" className="font-semibold text-grey70">
                          {section.section_label}
                        </Typography>
                      )}
                      <ul className="flex list-none flex-col gap-2 p-0">
                        {section.steps.map((step) => (
                          <ChecklistStepRow
                            key={step.step_id}
                            step={step}
                            blockerTitle={checklist.blockerTitle(step)}
                            isExpanded={expandedStepId === step.step_id}
                            onExpand={() => setExpansion(expandedStepId === step.step_id ? COLLAPSED : step.step_id)}
                            onOpenBlocker={() => setExpansion(blockingStepId(step))}
                            onSettle={(status) => settle(step, status)}
                            busy={checklist.pendingAction(step.step_id)}
                          />
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        ),
      })}
    </>
  );
}
